/**
 * What the plan trains: two silhouettes with the worked muscles lit, and a ranked readout beside
 * them.
 *
 * ── The figures are `shared/ui/bodyModel`'s, never an inline drawing ─────────────────────────
 * Owner decision **D4**. The canon's own figure is eleven rectangles and ellipses standing in for a
 * body at the reference's 59 x 106.5 footprint — a placeholder, not artwork. The two apps draw ONE
 * athlete: a muscle lit on the phone and grey on the desktop is a disagreement about the same data,
 * and the artwork is already vendored here from the mobile package.
 *
 * The frame keeps the silhouette's own 1:2 rule (`--h-silhouette` sets the height and the SVG takes
 * the width its viewBox implies), sized to the specimen's footprint.
 *
 * ── One `aria-hidden` drawing, and the ranking carries the words ─────────────────────────────
 * The figures and their two captions are decorative: everything they say is in the ranking's
 * accessible name, in order, once. `MuscleRanking` carries that name — see its header for the whole
 * decision. Mobile hides its own silhouettes for the same reason.
 *
 * ── The figure is MONOCHROME and the tracks are not ──────────────────────────────────────────
 * Owner, 16 September 2026. `baseFill` is the fourth step of `--silhouette-*` rather than
 * `--bg-sunken`: the body used to be painted in the pane's own sunken ground, so an untrained
 * figure read as a hole in the card rather than as a body. The worked steps come from
 * `model/muscleSplitView`, which is where the reasoning for all four lives.
 *
 * ── The reserve is on the SECTION, and an empty split still fills it ─────────────────────────
 * `--h-pl-muscle` is held from the first frame. A plan whose exercises name no muscle the catalogue
 * knows keeps that box and stands one neutral sentence in it — not a red state, and not a collapse.
 * A plan with no exercises at all is a plan the trainer has not finished, which is a fact rather
 * than a failure.
 */

import { useTranslation } from 'react-i18next';

import { numberFormat } from '~/shared/lib/intlCache';
import { BodySilhouette } from '~/shared/ui/bodyModel/BodySilhouette';
import skel from '~/shared/ui/skeleton.module.css';

import type { InspectorView } from '../model/inspectorView';
import { MuscleRanking } from './MuscleRanking';
import styles from './MuscleSection.module.css';

interface MuscleSectionProps {
  /** `null` while `/full` is still open. The section stands either way. */
  readonly view: InspectorView | null;
  readonly skeleton: boolean;
  /** `useSkeletonGate`'s hand-over class for the readout the answer brings — `''` before then. */
  readonly reveal: string;
}

/** The two sides, each with the caption the app already carries for it. */
const SIDES = [
  { side: 'front', labelKey: 'clients.detail.muscleMap.front' },
  { side: 'back', labelKey: 'clients.detail.muscleMap.back' },
] as const;

export function MuscleSection({ view, skeleton, reveal }: MuscleSectionProps) {
  const { t, i18n } = useTranslation();
  const segments = view?.muscle.segments ?? [];
  // The read has answered, and the answer is that no exercise in this plan names a muscle group the
  // catalogue knows. Different from "still waiting", which is what keeps the figures up.
  const emptySplit = view !== null && segments.length === 0;
  const percent = (value: number) =>
    numberFormat(i18n.language, { style: 'percent' }).format(value / 100);

  return (
    <section className={styles.section}>
      {/* A real heading, styled as the canon's eyebrow: the pane has two sections and a reader
        * user could not jump between them. The strip's expanded table already has a caption. */}
      <h3 className={styles.eyebrow}>{t('web:planLibrary.inspector.muscle.eyebrow')}</h3>

      <div className={styles.body}>
        {/* The pair of figures. They stand while the read is open and once it has answered with a
          * split — an unlit body is the honest drawing of a plan whose split has not arrived.
          *
          * They do NOT stand when the answer IS "no muscle groups": two anatomical drawings with
          * nothing lit beside a sentence read as artwork that failed to render, which is a defect
          * the trainer would report rather than a fact they would take. The section's own reserve
          * holds the box either way. */}
        {emptySplit ? null : (
        <span className={styles.figures} aria-hidden>
          {SIDES.map(({ side, labelKey }) => (
            <span key={side} className={styles.figureBox}>
              <BodySilhouette
                side={side}
                gender="male"
                fillBySlug={view?.muscle.fillBySlug ?? {}}
                baseFill="var(--silhouette-base)"
              />
              <span className={styles.figureCaption}>{t(labelKey)}</span>
            </span>
          ))}
        </span>
        )}

        <div className={styles.readout}>
          {view === null ? (
            skeleton ? (
              /* SIX bars, at a ranked row's own height and pitch, because six rows are what the
                * answer draws. The gate hides the bars, never the box. */
              <span className={styles.skelRanking}>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <i key={index} className={`${String(skel.bar)} ${String(styles.skelRow)}`} />
                ))}
              </span>
            ) : null
          ) : emptySplit ? (
            <p className={`${String(styles.empty)} ${reveal}`}>
              {t('web:planLibrary.inspector.muscle.empty')}
            </p>
          ) : (
            <MuscleRanking
              className={reveal}
              rows={segments}
              formatPercent={percent}
              spoken={t('web:planLibrary.inspector.muscle.spoken', {
                shares: segments
                  .map((segment) =>
                    t('web:planLibrary.inspector.muscle.share', {
                      name: segment.label,
                      percent: segment.percent,
                    }),
                  )
                  .join(', '),
              })}
            />
          )}
        </div>
      </div>
    </section>
  );
}
